import { useEffect, useRef, useCallback } from 'react';

interface UseIdleTimerOptions {
  timeoutMs: number;
  onIdle: () => void;
  enabled: boolean;
}

/**
 * 미입력 감지 타이머
 * - touchstart, mousedown, keydown 이벤트 발생 시 타이머 리셋
 * - timeoutMs 경과 시 onIdle 콜백 호출
 * - enabled가 false면 타이머 비활성화
 */
export function useIdleTimer({ timeoutMs, onIdle, enabled }: UseIdleTimerOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef = useRef(onIdle);

  // onIdle 콜백이 변경되어도 타이머를 재설정하지 않도록 ref 사용
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      onIdleRef.current();
    }, timeoutMs);
  }, [clearTimer, timeoutMs]);

  const resetTimer = useCallback(() => {
    if (enabled) {
      startTimer();
    }
  }, [enabled, startTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    startTimer();

    const handleActivity = () => {
      startTimer();
    };

    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      clearTimer();
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [enabled, startTimer, clearTimer]);

  return { resetTimer };
}
